/**
 * AnnouncementBanner Component
 * LAYER-SHARED-INTERACTIONS
 *
 * Displays announcement messages at the top of the page.
 * Supports different types and dismissible functionality.
 */
import React from 'react';
import {
  InformationCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { Announcement, AnnouncementType } from '../../hooks/useAnnouncements';
import './AnnouncementBanner.scss';

export interface AnnouncementBannerProps {
  /** Announcement data */
  announcement: Announcement;
  /** Callback when dismissed */
  onDismiss?: () => void;
  /** Additional class name */
  className?: string;
}

const typeIcons: Record<AnnouncementType, React.ComponentType<{ className?: string }>> = {
  info: InformationCircleIcon,
  warning: ExclamationTriangleIcon,
  success: CheckCircleIcon,
  error: XCircleIcon,
};

/**
 * AnnouncementBanner displays a single announcement.
 *
 * @example
 * <AnnouncementBanner
 *   announcement={{
 *     id: '1',
 *     message: 'Scheduled maintenance on Saturday',
 *     type: 'warning',
 *     dismissible: true,
 *     enabled: true,
 *   }}
 *   onDismiss={() => dismiss('1')}
 * />
 */
export const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({
  announcement,
  onDismiss,
  className = '',
}) => {
  const { type, title, message, dismissible, link } = announcement;
  const Icon = typeIcons[type];

  return (
    <div
      className={`announcement-banner announcement-banner--${type} ${className}`}
      role="alert"
    >
      <div className="announcement-banner__content">
        <Icon className="announcement-banner__icon" />
        <div className="announcement-banner__text">
          {title && <strong className="announcement-banner__title">{title}</strong>}
          <span className="announcement-banner__message">{message}</span>
          {link && (
            <a
              href={link.url}
              className="announcement-banner__link"
              target={link.url.startsWith('http') ? '_blank' : undefined}
              rel={link.url.startsWith('http') ? 'noopener noreferrer' : undefined}
            >
              {link.text}
            </a>
          )}
        </div>
      </div>

      {dismissible && onDismiss && (
        <button
          type="button"
          className="announcement-banner__dismiss"
          onClick={onDismiss}
          aria-label="Dismiss announcement"
        >
          <XMarkIcon />
        </button>
      )}
    </div>
  );
};

/**
 * AnnouncementStack displays multiple announcements stacked.
 */
export interface AnnouncementStackProps {
  /** Announcements to display */
  announcements: Announcement[];
  /** Callback when an announcement is dismissed */
  onDismiss?: (id: string) => void;
  /** Maximum announcements to show */
  maxVisible?: number;
  /** Additional class name */
  className?: string;
}

export const AnnouncementStack: React.FC<AnnouncementStackProps> = ({
  announcements,
  onDismiss,
  maxVisible = 3,
  className = '',
}) => {
  const visible = announcements.slice(0, maxVisible);

  if (visible.length === 0) {
    return null;
  }

  return (
    <div className={`announcement-stack ${className}`}>
      {visible.map(announcement => (
        <AnnouncementBanner
          key={announcement.id}
          announcement={announcement}
          onDismiss={onDismiss ? () => onDismiss(announcement.id) : undefined}
        />
      ))}
    </div>
  );
};

export default AnnouncementBanner;
